import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Html5Qrcode } from "html5-qrcode";
import { AlertTriangle, Camera, Keyboard, RefreshCw } from "lucide-react";

const DIV_ID = "barcode-scanner-region";

function detectError(err) {
  const name = err?.name || "";
  const msg = String(err?.message || err || "");
  if (name === "NotAllowedError" || msg.includes("Permission")) {
    return { title: "لم يتم منح صلاحية الكاميرا", hint: "اضغط على أيقونة القفل بجانب الرابط في المتصفح ← اسمح بالكاميرا، ثم أعد المحاولة. على iPhone: الإعدادات ← Safari ← الكاميرا ← اسمح." };
  }
  if (name === "NotFoundError" || msg.includes("Requested device not found")) {
    return { title: "لا توجد كاميرا متاحة", hint: "الجهاز لا يحتوي على كاميرا أو أنها مستخدمة من تطبيق آخر." };
  }
  if (name === "NotReadableError" || msg.includes("in use")) {
    return { title: "الكاميرا مشغولة", hint: "أغلق أي تطبيق آخر يستخدم الكاميرا (واتساب/زوم/...) ثم أعد المحاولة." };
  }
  if (name === "OverconstrainedError") {
    return { title: "لا توجد كاميرا خلفية", hint: "جرب اختيار الكاميرا الأمامية من القائمة أدناه." };
  }
  if (msg.includes("secure") || msg.includes("https")) {
    return { title: "الاتصال غير آمن", hint: "الكاميرا تحتاج HTTPS. تأكد أن الرابط يبدأ بـ https://" };
  }
  return { title: "تعذر تشغيل الكاميرا", hint: msg || "خطأ غير معروف" };
}

export default function BarcodeScannerDialog({ open, onClose, onScan }) {
  const scannerRef = useRef(null);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState("");
  const [error, setError] = useState(null);
  const [manual, setManual] = useState("");
  const [inIframe, setInIframe] = useState(false);
  const [starting, setStarting] = useState(false);
  const cancelledRef = useRef(false);

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch {}
        try { await scannerRef.current.clear(); } catch {}
        scannerRef.current = null;
      }
    } catch {}
  };

  const startWith = async (deviceId) => {
    setError(null);
    setStarting(true);
    await stopScanner();
    try {
      const scanner = new Html5Qrcode(DIV_ID);
      scannerRef.current = scanner;
      const config = { fps: 10, qrbox: { width: 260, height: 160 }, aspectRatio: 1.7 };
      const cam = deviceId ? { deviceId: { exact: deviceId } } : { facingMode: "environment" };
      await scanner.start(cam, config, (text) => {
        if (cancelledRef.current) return;
        cancelledRef.current = true;
        stopScanner().then(() => onScan(text));
      }, () => {});
    } catch (e) {
      setError(detectError(e));
    } finally { setStarting(false); }
  };

  const requestAndList = async () => {
    setError(null);
    try {
      // Trigger permission prompt explicitly
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      s.getTracks().forEach(t => t.stop());
      const devs = await Html5Qrcode.getCameras();
      setCameras(devs || []);
      const back = (devs || []).find(d => /back|rear|environment|خلف/i.test(d.label));
      const first = back?.id || devs?.[0]?.id || "";
      setCameraId(first);
      await startWith(first);
    } catch (e) {
      setError(detectError(e));
    }
  };

  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    try { setInIframe(window.self !== window.top); } catch { setInIframe(true); }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError({ title: "المتصفح لا يدعم الكاميرا", hint: "استخدم متصفحاً حديثاً (Chrome/Safari/Edge)." });
      return;
    }
    if (!window.isSecureContext) {
      setError({ title: "الاتصال غير آمن", hint: "الكاميرا تحتاج HTTPS. الرابط الحالي غير آمن." });
      return;
    }
    requestAndList();
    return () => { cancelledRef.current = true; stopScanner(); };
    // eslint-disable-next-line
  }, [open]);

  const changeCamera = async (id) => {
    setCameraId(id);
    await startWith(id);
  };

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    onScan(manual.trim());
    setManual("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5" /> مسح الباركود بالكاميرا</DialogTitle>
          <DialogDescription>وجّه الكاميرا نحو الباركود — أو استخدم الإدخال اليدوي كبديل.</DialogDescription>
        </DialogHeader>

        {inIframe && (
          <div className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-3 text-xs flex gap-2" data-testid="iframe-warning">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>الكاميرا قد لا تعمل داخل هذه النافذة. افتح الرابط <b>مباشرة في المتصفح</b> أو بعد <b>تثبيت التطبيق</b> على الشاشة الرئيسية.</div>
          </div>
        )}

        <div id={DIV_ID} className={`w-full rounded-md overflow-hidden bg-black min-h-[240px] ${error ? "hidden" : ""}`} />
        {starting && !error && <div className="text-center text-sm text-muted-foreground">جاري تشغيل الكاميرا...</div>}

        {error && (
          <div className="space-y-3" data-testid="camera-error">
            <div className="border border-destructive/30 bg-destructive/5 rounded-md p-4">
              <div className="flex items-center gap-2 font-bold text-destructive mb-1">
                <AlertTriangle className="w-4 h-4" /> {error.title}
              </div>
              <div className="text-sm text-muted-foreground leading-relaxed">{error.hint}</div>
            </div>
            <Button variant="outline" onClick={requestAndList} className="w-full" data-testid="retry-camera-button">
              <RefreshCw className="w-4 h-4 me-2" /> إعادة المحاولة
            </Button>
          </div>
        )}

        {cameras.length > 1 && !error && (
          <div>
            <Label className="text-xs">اختر الكاميرا</Label>
            <Select value={cameraId} onValueChange={changeCamera}>
              <SelectTrigger data-testid="camera-select"><SelectValue placeholder="اختر" /></SelectTrigger>
              <SelectContent>
                {cameras.map(c => <SelectItem key={c.id} value={c.id}>{c.label || `كاميرا ${c.id.slice(0,6)}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="border-t border-border pt-3">
          <form onSubmit={submitManual} className="space-y-2">
            <Label className="text-xs flex items-center gap-1"><Keyboard className="w-3.5 h-3.5" /> إدخال يدوي</Label>
            <div className="flex gap-2">
              <Input value={manual} onChange={(e)=>setManual(e.target.value)} placeholder="اكتب رقم الباركود يدوياً..." className="font-mono" data-testid="manual-barcode-input" />
              <Button type="submit" disabled={!manual.trim()} data-testid="manual-barcode-submit">استخدم</Button>
            </div>
          </form>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
